"use client";

/**
 * Simple Drawer Component
 * 
 * A basic Drawer component that's compatible with Drawer.Stack.
 * This uses the simple Drawer component (not compound components) so it can be used
 * with Mantine's Drawer.Stack for proper stacking management.
 */

import * as React from "react";
import { Drawer } from '@mantine/core';
import { cn } from "@/lib/utils.js";

export interface SimpleDrawerProps {
  /** Whether the drawer is open */
  opened: boolean;
  /** Callback when the drawer should be closed */
  onClose: () => void;
  /** The title of the drawer */
  title?: string;
  /** The content of the drawer */
  children: React.ReactNode;
  /** Drawer position */
  position?: "top" | "bottom" | "left" | "right";
  /** Drawer size */
  size?: string | number;
  /** Whether to close on escape key */
  closeOnEscape?: boolean;
  /** Whether to close on click outside */
  closeOnClickOutside?: boolean;
  /** Whether to trap focus */
  trapFocus?: boolean;
  /** Whether to return focus */
  returnFocus?: boolean;
  /** Custom class name */
  className?: string;
  /** Stack ID for Drawer.Stack */
  stackId?: string;
}

export const SimpleDrawer: React.FC<SimpleDrawerProps> = ({
  opened,
  onClose,
  title,
  children,
  position = "bottom",
  size = "md",
  closeOnEscape = true,
  closeOnClickOutside = true,
  trapFocus = true,
  returnFocus = true,
  className,
  stackId,
  ...props
}) => {
  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={title}
      position={position}
      size={size}
      closeOnEscape={closeOnEscape}
      closeOnClickOutside={closeOnClickOutside}
      trapFocus={trapFocus}
      returnFocus={returnFocus}
      classNames={{
        content: cn("flex flex-col", className)
      }}
      {...props}
    >
      {children}
    </Drawer>
  );
};
